import * as THREE from 'three';
import { Enemy, type DropTableEntry } from './Enemy.js';
import type { Game } from '../../game/Game.js';
import type { Circle } from '../../systems/Collision.js';

/**
 * Runner drop table.
 *
 * From design doc:
 * - Transport drops: common (60%)
 *   - Treads: 25%
 *   - Hover: 20%
 *   - Legs: 15%
 * - Weapon drops: rare (20%)
 *   - Repeater: 8%
 *   - Lancer: 6%
 *   - Sprayer: 6%
 * - No drop: 20%
 */
const RUNNER_DROPS: DropTableEntry[] = [
  { partId: 'treads', weight: 20 },
  { partId: 'hover', weight: 18 },
  { partId: 'legs', weight: 12 },
  { partId: 'repeater', weight: 5 },
  { partId: 'lancer', weight: 4 },
  { partId: 'sprayer', weight: 4 },
  { partId: 'needler', weight: 5 },  // Fast weapon fits fast enemy
  { partId: 'shocker', weight: 4 },
  { partId: 'mortar', weight: 3 },
  { partId: null, weight: 25 }, // No drop
];

/**
 * Runner enemy - fast melee attacker.
 *
 * Behavior:
 * - Constantly moves toward player (fast)
 * - When close enough, performs melee attack
 * - Glass cannon - low HP but high damage
 *
 * Visual:
 * - Orange/red color
 * - Smaller, more agile looking
 * - Legs or wheels (cylinder/spheres)
 * - Size: ~0.6 units
 *
 * Stats:
 * - HP: 30 (glass cannon)
 * - Speed: 5 units/sec (fast)
 * - Attack damage: 20
 * - Attack range: 1.2 units (melee)
 * - Attack rate: 1.5 attacks/sec
 */
export class Runner extends Enemy {
  /** Movement speed in units per second */
  private readonly speed: number = 5;

  /** Damage dealt per melee attack */
  private readonly attackDamage: number = 20;

  /** Time until next attack can be performed (seconds) */
  private attackCooldown: number = 0;

  /** How close the runner needs to be to attack */
  private readonly attackRange: number = 1.2;

  /** Attacks per second */
  private readonly attackRate: number = 1.5;

  /** Collision radius for hitbox */
  private readonly collisionRadius: number = 0.3;

  /** Telegraph duration before melee attack (seconds) */
  private readonly telegraphDuration: number = 0.2;

  /** Whether ready to attack after telegraph */
  private readyToAttack: boolean = false;

  constructor(x: number, y: number, game: Game) {
    super(x, y, 30, RUNNER_DROPS, game);
    this.damage = this.attackDamage; // Set base damage for scaling
    this.createMesh();
  }

  /**
   * Create the runner's visual mesh.
   *
   * Visual structure:
   * - Small, agile-looking body
   * - Cylindrical body with spheres for legs/wheels
   * - Orange/red enemy color
   */
  private createMesh(): void {
    const group = new THREE.Group();

    // Body - small cylinder
    const bodyGeometry = new THREE.CylinderGeometry(0.3, 0.3, 0.4, 16);
    const bodyMaterial = new THREE.MeshStandardMaterial({
      color: 0xff5722, // Orange-red enemy color
      metalness: 0.5,
      roughness: 0.6,
    });
    const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
    body.position.y = 0.3; // Slightly above ground
    group.add(body);

    // "Legs" - small spheres for wheel/leg effect
    const legGeometry = new THREE.SphereGeometry(0.12, 8, 8);
    const legMaterial = new THREE.MeshStandardMaterial({
      color: 0xe64a19, // Darker orange-red
      metalness: 0.6,
      roughness: 0.5,
    });

    // Front legs
    const frontLeft = new THREE.Mesh(legGeometry, legMaterial);
    frontLeft.position.set(-0.2, 0.1, 0.25);
    group.add(frontLeft);

    const frontRight = new THREE.Mesh(legGeometry, legMaterial);
    frontRight.position.set(0.2, 0.1, 0.25);
    group.add(frontRight);

    // Back legs
    const backLeft = new THREE.Mesh(legGeometry, legMaterial);
    backLeft.position.set(-0.2, 0.1, -0.25);
    group.add(backLeft);

    const backRight = new THREE.Mesh(legGeometry, legMaterial);
    backRight.position.set(0.2, 0.1, -0.25);
    group.add(backRight);

    this.mesh = group;
    this.updateMeshTransform();
  }

  update(dt: number): void {
    const dtSec = dt / 1000;

    // Update telegraph animation (crouch/lunge)
    const telegraphComplete = this.updateTelegraph(dtSec);
    if (telegraphComplete) {
      this.readyToAttack = true;
    }

    // Apply crouch visual during telegraph
    if (this.isTelegraphing && this.mesh) {
      // Scale down slightly to simulate crouch
      const t = this.telegraphTimer / this.telegraphDuration;
      const crouchScale = 1 - (1 - t) * 0.3; // Crouch to 70% at peak
      this.mesh.scale.y = crouchScale;
    } else if (this.mesh) {
      // Reset scale
      this.mesh.scale.y = 1;
    }

    // Decrement attack cooldown
    if (this.attackCooldown > 0) {
      this.attackCooldown -= dtSec;
    }

    // Find the player
    const player = this.findPlayer();
    if (!player) {
      return;
    }

    // Calculate distance to player
    const dx = player.position.x - this.position.x;
    const dy = player.position.y - this.position.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    // Check if close enough to start telegraph or attack
    if (dist <= this.attackRange && this.attackCooldown <= 0) {
      // Start telegraph when in range
      if (!this.isTelegraphing && !this.readyToAttack) {
        this.startTelegraph(this.telegraphDuration);
      }

      // Execute attack when telegraph completes
      if (this.readyToAttack) {
        this.meleeAttack();
        this.attackCooldown = 1 / this.attackRate;
        this.readyToAttack = false;
      }
    }

    // Don't move while telegraphing (frozen in crouch)
    if (!this.isTelegraphing) {
      this.moveTowardPlayer(dtSec);
    }

    this.updateMeshTransform();
  }

  /**
   * Move toward the player.
   *
   * @param dtSec - Delta time in seconds
   */
  private moveTowardPlayer(dtSec: number): void {
    const player = this.findPlayer();
    if (!player) return;

    const dx = player.position.x - this.position.x;
    const dy = player.position.y - this.position.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist > 0) {
      // Normalize direction
      const nx = dx / dist;
      const ny = dy / dist;

      // Move toward player
      this.position.x += nx * this.speed * dtSec;
      this.position.y += ny * this.speed * dtSec;

      // Clamp to room bounds
      const room = this.game.getRoom();
      this.position = room.clampPosition(this.position, this.collisionRadius);

      // Face player
      this.rotation = Math.atan2(dy, dx);
    }
  }

  /**
   * Perform a melee attack on the player.
   *
   * Deals damage directly without projectiles.
   */
  private meleeAttack(): void {
    const player = this.findPlayer();
    if (!player) return;

    // Deal damage directly (no projectile for melee)
    // Player must be a Mech to have takeDamage method
    if ('takeDamage' in player && typeof player.takeDamage === 'function') {
      (player as any).takeDamage(this.damage); // Use base damage (may be scaled)
    }
  }

  /**
   * Find the player entity in the game.
   *
   * Returns null if no player found.
   */
  private findPlayer(): { position: { x: number; y: number } } | null {
    return (this.game as any).playerMech || null;
  }

  getCollisionShape(): Circle {
    return {
      x: this.position.x,
      y: this.position.y,
      radius: this.collisionRadius,
    };
  }
}
